import { Component } from 'react';
import "./BookingDetailsWidget.scss"
import IbssSvgIcon from '../../../../Components/Icons/SvgIcon/IbssSvgIcon';
import { Icons } from '../../../../Common/AllsvgIcons';
import { DateTime } from 'luxon';
import { appContext } from '../../../../AppContext';
import IbssInputDropDown from '../../../../Components/Inputs/SelectList/IbssInputDropDown';
import { ISpace } from './DataModels';
import Helper, { getFloorUsingFloorId } from '../../../../Common/Helper';
import IbssButton from '../../../../Components/Buttons/Button/IbssButton';
import { Box, Card, CardContent, Container, Grid, Link, Skeleton, Typography } from '@mui/material';
import { rights } from '../../../../Common/RightsManager';
import { CostCodeWithAllocation } from '../../../../Components/Dialogs/CostCodesDialog/CostCodesDialog';

class BookingDetailsWidget extends Component<IProps, IState>
{

    private get labels() { return appContext().labels; }
    private get local() { return appContext().localStorageProvider}
    private canApproveCatering: boolean;

    constructor(props: IProps)
    {
        super(props);

        this.canApproveCatering = rights.API.Catering.Approve.has(this.props.buildingId);
        this.state =
        {
            selectedSpaceId: '',
            selectedFloorId: 0,
            spaceOptions: [],
            floorOptions: [],
            currentFloorName: ''
        };
    }

    public componentDidMount(): void 
    {
        this.setState({
            selectedSpaceId: this.props.spaceId,
        });
        this.populateFloors(this.props.buildingId);
        this.populateSpaceOptions();
    }
    
    public componentDidUpdate(prevProps: IProps, prevState: IState): void 
    {
        if(this.props.spaceId !== prevProps.spaceId && this.props.spaceId)
            {
                const newSpace = this.props.spaces.find(space => this.props.spaceId.split(';')[0] === space.Space_Id);
                const newFloorId = newSpace?.Node_Id ?? 0;

                this.setState({
                    selectedSpaceId: this.props.spaceId,
                    selectedFloorId: newFloorId,
                    currentFloorName: getFloorUsingFloorId(newFloorId),
            });
        }

        if(this.props.buildingId !== prevProps.buildingId && this.props.buildingId)
        {
            this.populateFloors(this.props.buildingId);
        }

        if(this.props.spaces.length !== prevProps.spaces.length)
        {
            this.populateSpaceOptions();
        }
    }

    private populateFloors(selectedBuildingId: number): void
    {
        const floors = Helper.getFloorsByBuildingId(selectedBuildingId);

        const options = floors
            .map(i => ({ label: i.Node_Name, value: i.Node_Id }))
            .sort((a, b) => (a.label.toLocaleLowerCase() < b.label.toLocaleLowerCase() ? - 1 : 1)); // sort by name

        this.setState({ floorOptions: options });
    }

    private populateSpaceOptions(): void
    {
        const spaceOptions = this.props.spaces.map(space => ({label: space.Space_Name, value: space.Space_Id, nodeId: space.Node_Id }));
        this.setState({ spaceOptions: spaceOptions });
    }

    private changeFloor(floorId: number): void
    {
        this.setState({selectedFloorId: floorId})
    }
    
    private changeSpace(spaceId: string): void
    {
        this.setState({selectedSpaceId: spaceId});
    }

    private confirmChangeSpace(): void
    {
        const selectedSpace = this.state.spaceOptions.filter(space => space.value === this.state.selectedSpaceId)[0];
        const { nodeId, label, value } = selectedSpace;
        this.props.changeCateringSpace(nodeId, value, label);
    }

    private get visibleSpaceOptions(): ISpaceOption[]
    {
        return this.state.spaceOptions.filter(space => space.nodeId === this.state.floorOptions.filter(floor => floor.value === this.state.selectedFloorId)[0]?.value)
    }

    public render(): JSX.Element
    {
        const canBypassCateringCutoffRights = this.props.userHasIgnoreCateringCutoffRights;
        return (
            <>
            {this.props.skeletonLoad ? <BookingDetailsSkeleton /> :
            <Grid container spacing={2}>
                <Grid item xl={4} lg={6} md={12}>
                    <Card sx={{ display: 'flex', backgroundColor: 'var(--ui-background)',  minHeight: { xs: 250, md: 250, lg: 350 } }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                                <Box pr={2}>
                                    {
                                        this.props.spaceImageUrl && this.props.spaceImageUrl.length > 0 ?
                                            <img style={{ width: 75, height: 75, borderRadius: 4 }} src={this.props.spaceImageUrl} />
                                            :
                                            <Box sx={{ width: 75, height: 75, borderRadius: 4, background: 'grey' }}></Box>
                                    }
                                </Box>
                                <Box>
                                    <Typography variant='h6'>{this.labels.funcRelatedSpaceBooking_L}</Typography>
                                    <Typography variant='body1'>{this.labels.funcLinkedCateringOrders_D}</Typography>

                                    <Grid container my={1}>
                                        <Grid item xl={6} lg={12}>
                                            <Box display={'flex'} alignItems={'center'} whiteSpace={'nowrap'}>
                                            <IbssSvgIcon fontSize='inherit' >
                                                {Icons.MapPinIcon}
                                            </IbssSvgIcon>
                                            <Typography variant='body1'mx={1}>{this.props.spaceName}</Typography>
                                            {
                                                this.props.host != "" &&
                                                <Box display={'flex'}>
                                                    <Typography variant='body1' mx={1}>{this.labels.HubLabelHost}: {this.props.host}</Typography>
                                                    <Typography variant='body1'>•</Typography>
                                                </Box>
                                            }
                                            </Box>
                                        </Grid>
                                        <Grid item xl={6} lg={12}>
                                            <Box display={'flex'} alignItems={'center'} whiteSpace={'nowrap'}>
                                                <IbssSvgIcon fontSize='small'>
                                                    {Icons.FloorIcon}
                                                </IbssSvgIcon>
                                                <Typography variant='body1' mx={1}>{this.state.currentFloorName}</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xl={12}>
                                            <Box display={'flex'} alignItems={'center'} whiteSpace={'nowrap'}>
                                                <IbssSvgIcon fontSize='inherit' >
                                                    {Icons.UserIcon}
                                                </IbssSvgIcon>
                                                <Typography variant='body1' mx={1}>{this.labels.HubLabelOwner}: {this.props.owner}</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xl={5} lg={12}>
                                            <Box display={'flex'} alignItems={'center'} whiteSpace={'nowrap'}>
                                                <IbssSvgIcon fontSize='inherit' >
                                                    {Icons.calenderIcon}
                                                </IbssSvgIcon>
                                                <Typography variant='body1' mx={1}>{this.props.start.toLocaleString(DateTime.DATE_SHORT)}</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xl={7} lg={12} mb={1}>
                                            <Box display={'flex'} alignItems={'center'} whiteSpace={'nowrap'}>
                                                <IbssSvgIcon fontSize='inherit' >
                                                    {Icons.TimeIcon}
                                                </IbssSvgIcon>
                                                <Typography variant='body1' mr={1}>{this.props.start.toLocaleString(DateTime.TIME_24_SIMPLE)} - {this.props.end.toLocaleString(DateTime.TIME_24_SIMPLE)}</Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                    <IbssButton
                                        className='mr-2'
                                        variant={'contained'}
                                        color='secondary'
                                        onClick={() => this.props.viewBooking()}
                                    >
                                        {this.labels.funcViewBooking_S}
                                    </IbssButton>
                                    {!this.props.pastCutOffPeriod &&
                                    <Box display={'flex'} alignItems={'center'} gap={1}>
                                        {this.props.cutOffTime.isValid &&
                                            <>
                                                <Typography variant='body1' fontWeight={'600'} my={1}>{this.labels.funcOrderBy_L}</Typography> 
                                                <Typography variant='body1' my={1}> {this.props.cutOffTime?.toLocaleDateString()} {this.labels.funcAt_S} {this.props.cutOffTime?.toLocaleTimeString()}</Typography>
                                            </>
                                        }
                                    </Box>
                                    }
                                    {!canBypassCateringCutoffRights && this.props.pastCutOffPeriod &&
                                        <Typography sx={{color: "var(--ui-error)"}} variant='body1' my={1}>{this.labels.funcOrderChangesClosed_D}</Typography>
                                    
                                    }
                                    {canBypassCateringCutoffRights &&
                                        <Typography sx={{color: "var(--ui-error)"}} variant='body1' my={1}>{this.labels.funcBypassOrderPolicies_D}</Typography>
                                    }
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                {(this.props.userHasCateringRights && !this.props.newOrder) && 
                <Grid item xl={4} lg={6} md={12}>
                    <Card sx={{ display: 'flex', backgroundColor: 'var(--ui-background)',  minHeight: { xs: 250, md: 250, lg: 350 } }}>
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item md={12}>
                                    <Typography variant='h6'>{this.labels.funcServiceLocation_L}</Typography>
                                    <Typography variant='body1'>{this.labels.funcDefaultCateringLocation_D}</Typography>
                                </Grid>
                                <Grid item md={6}>
                                    <IbssInputDropDown
                                        inputLabel={this.labels.HubLabelFloor}
                                        options={this.state.floorOptions}
                                        value={this.state.selectedFloorId}
                                        id={"floorSelection"}
                                        onChange={e => this.changeFloor(e.target.value)}
                                    />
                                </Grid>
                                <Grid item md={6}>
                                    <IbssInputDropDown
                                        inputLabel={this.labels.HubLabelSpace}
                                        options={this.visibleSpaceOptions}
                                        value={this.state.selectedSpaceId}
                                        id={"spaceSelection"}
                                        onChange={e => this.changeSpace(e.target.value)}
                                    />
                                </Grid>
                                <Grid item md={12}>
                                <IbssButton
                                    disabled={this.props.spaceId === this.state.selectedSpaceId}
                                    variant={'contained'}
                                    onClick={() => this.confirmChangeSpace()}
                                >
                                    {this.labels.funcSaveAlternameLocation_L}
                                </IbssButton>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>}
                <Grid item xl={4} lg={6} md={12}>
                    <Card sx={{ display: 'flex', backgroundColor: 'var(--ui-background)',  minHeight: { xs: 250, md: 250, lg: 350 } }}>
                        <CardContent>
                            <Typography variant='h6'>{this.labels.funcTotalCateringOrders_L}</Typography>
                            <Typography variant='body1'>{this.labels.funcTotalItemsCost_D}</Typography>
                            <Box display={'flex'} alignItems={'center'}>
                                <Typography variant='body1'>{this.labels.funcItems_S}</Typography>
                                <Typography variant='h6' mx={1}>{this.props.getAllOrdersItemTotals()}</Typography>
                            </Box>
                            <Box display={'flex'} alignItems={'center'}>
                                <Typography variant='body1'>{this.labels.funcTotalValue_L}</Typography>
                                <Typography variant='h6' mx={1}>{this.props.currency}{this.props.getAllOrdersCostTotals()}</Typography>
                            </Box>
                            {rights.API.Catering.AssignCostCode.has() &&
                            <Box display={'flex'} alignItems={'center'}>
                                <Typography variant='body1'>{this.labels.HubLabelCostCodes}</Typography> 
                                {this.props.costCodes.length > 0 ? 
                                <Link onClick={() => this.props.showCostCodeModal()} mx={1} sx={{ cursor: 'pointer' }}>
                                    {this.props.costCodes.map((item) => item.costCode).join(", ")}
                                </Link>
                                : <IbssButton onClick={() => this.props.showCostCodeModal()}>{this.labels.HubButtonAdd}</IbssButton>}
                            </Box>}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>}
            </>
        )
    }
}

export default BookingDetailsWidget;

export class BookingDetailsSkeleton extends Component
{
    public render(): JSX.Element
    {
        return (
            <Grid container spacing={2}>
                <Grid item xs={4}>
                    <Card sx={{ display: 'flex', backgroundColor: 'var(--ui-background)', height: '250px' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                                <Skeleton variant="rectangular" width={200} height={200} />
                            </Box>
                        </CardContent>
                        <div style={{ width: '100%' }}>
                            <Skeleton sx={{ margin: '15px', marginBottom: '10px' }} variant="rectangular" width={'80%'} height={50} />
                            <Skeleton sx={{ margin: '15px', marginBottom: '10px' }} variant="rectangular" width={'50%'} height={30} />
                            <Skeleton sx={{ margin: '15px' }} variant="rectangular" width={'20%'} height={20} />
                        </div>
                    </Card>
                </Grid>
                <Grid item xs={4}>
                    <Card sx={{ display: 'flex', backgroundColor: 'var(--ui-background)', height: '250px' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                                <Skeleton variant="rectangular" width={200} height={200} />
                            </Box>
                        </CardContent>
                        <div style={{ width: '100%' }}>
                            <Skeleton sx={{ margin: '15px', marginBottom: '10px' }} variant="rectangular" width={'80%'} height={50} />
                            <Skeleton sx={{ margin: '15px', marginBottom: '10px' }} variant="rectangular" width={'50%'} height={30} />
                            <Skeleton sx={{ margin: '15px' }} variant="rectangular" width={'20%'} height={20} />
                        </div>
                    </Card>
                </Grid>
                                <Grid item xs={4}>
                    <Card sx={{ display: 'flex', backgroundColor: 'var(--ui-background)', height: '250px' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                                <Skeleton variant="rectangular" width={200} height={200} />
                            </Box>
                        </CardContent>
                        <div style={{ width: '100%' }}>
                            <Skeleton sx={{ margin: '15px', marginBottom: '10px' }} variant="rectangular" width={'80%'} height={50} />
                            <Skeleton sx={{ margin: '15px', marginBottom: '10px' }} variant="rectangular" width={'50%'} height={30} />
                            <Skeleton sx={{ margin: '15px' }} variant="rectangular" width={'20%'} height={20} />
                        </div>
                    </Card>
                </Grid>
            </Grid>
        )
    }
}


export interface IProps
{
    skeletonLoad: boolean;
    buildingId: number;
    spaceName: string;
    spaceImageUrl: string;
    spaceId: string;
    spaces: ISpace[];
    start: DateTime;
    end: DateTime;
    host: string;
    owner: string;
    currency: string;
    changeCateringSpace: (nodeId: number, spaceId: string, spaceName: string) => void;
    userHasCateringRights: boolean;
    newOrder: boolean;
    viewBooking: () => void;
    costCodes: CostCodeWithAllocation[];
    getAllOrdersCostTotals: () => string;
    getAllOrdersItemTotals: () => number;
    showCostCodeModal: () => void;
    cutOffTime: DateTime;
    cutOffTimeDuration: string;
    pastCutOffPeriod: boolean;
    userHasIgnoreCateringCutoffRights: boolean;
}

export interface IState
{
    selectedSpaceId: string;
    currentFloorName: string;
    selectedFloorId: number;
    spaceOptions: ISpaceOption[];
    floorOptions: IFloorOption[];
}

interface ISpaceOption
{
    label: string;
    value: string;
    nodeId: number;
}

interface IFloorOption
{
    label: string;
    value: number;
}